import produce from "immer"
import {ENVIRONMENT_FAILURES} from "@/lib/cameraFailure"
import {identityReducer, actionTypes as identityActionTypes, finaliseAttempt} from "@/api/identity/IdentityReducer"
import {incomeReducer, actionTypes as incomeActionTypes} from "@/api/income/IncomeReducer"
import {generateTheme} from "@/theme/theme"

export const exchangeTokenFailures = {
  tokenNotFound: "tokenNotFound",
  unavailable: "unavailable"
}

const classifyExchangeTokenFailure = (error) => {
  const tokenRejected = error?.response?.status === 400 &&
    error.response.data?.error?.errors?.some(({field}) => field === "token")
  return tokenRejected ? exchangeTokenFailures.tokenNotFound : exchangeTokenFailures.unavailable
}

export const actionTypes = {
  onAppContextMounted: "onAppContextMounted",
  onLoadApiKey: "onLoadApiKey",
  onLoadApiKeyCompleted: "onLoadApiKeyCompleted",
  onLoadApiKeyFailed: "onLoadApiKeyFailed",
  onTokenUpdated: "onTokenUpdated",
  onTokenSubmitted: "onTokenSubmitted",
  onTenantConfigUpdated: "onTenantConfigUpdated",
  onNoTokenFound: "onNoTokenFound",
  exchangeToken: "exchangeToken",
  exchangeTokenCompleted: "exchangeTokenCompleted",
  exchangeTokenFailed: "exchangeTokenFailed",
  cameraMounted: "cameraMounted",
  cameraOpening: "cameraOpening",
  cameraOpened: "cameraOpened",
  cameraUnmounted: "cameraUnmounted",
  captureJourneyStarted: "captureJourneyStarted",
  faceCaptureInitStarted: "faceCaptureInitStarted",
  faceCaptureInitialized: "faceCaptureInitialized",
  faceCaptureCapturing: "faceCaptureCapturing",
  faceCaptureCaptured: "faceCaptureCaptured",
  blockedCameraDetected: "blockedCameraDetected",
  blockedCameraDetectedFailed: "blockedCameraDetectedFailed",
  blockedCameraDetectedCompleted: "blockedCameraDetectedCompleted",
  captureLiveFaceFailedToLoad: "captureLiveFaceFailedToLoad",
  logUserJourneyEvent: 'logUserJourneyEvent',
  logUserJourneyEventCompleted: 'logUserJourneyEventCompleted',
  logUserJourneyEventFailed: 'logUserJourneyEventFailed',
  captureLiveFaceTimedOut: "captureLiveFaceTimedOut",
  onUnexpectedCaptureLiveFaceError: "onUnexpectedCaptureLiveFaceError",
  noConfigurationProfileFound: "noConfigurationProfileFound",
  cameraInitializationTimedOut: "cameraInitializationTimedOut",
  resetCameraInitialization: "resetCameraInitialization",
  cameraErrorReceived: "cameraErrorReceived",
  cameraFailureClassified: "cameraFailureClassified",
  watchCameraPermission: "watchCameraPermission",
  unwatchCameraPermission: "unwatchCameraPermission",
  cameraPermissionStateChanged: "cameraPermissionStateChanged",
  captureLocationRequested: "captureLocationRequested",
  captureLocationSuccess: "captureLocationSuccess",
  onLoadWebAuthnCapabilities: "onLoadWebAuthnCapabilities",
  onLoadWebAuthnCapabilitiesCompleted: "onLoadWebAuthnCapabilitiesCompleted",
  onLoadWebAuthnCapabilitiesFailed: "onLoadWebAuthnCapabilitiesFailed",
  sdkLoadStarted: "sdkLoadStarted",
  sdkLoadCompleted: "sdkLoadCompleted",
  sdkLoadFailed: "sdkLoadFailed",
  cdnLoadRetryAttempt: "cdnLoadRetryAttempt",
  cdnLoadSuccess: "cdnLoadSuccess",
  cdnLoadFailedAfterRetries: "cdnLoadFailedAfterRetries",
  recordedIdentifierSelected: "recordedIdentifierSelected",
  manualIdentifierSelected: "manualIdentifierSelected",
  checkDeploymentVersion: "checkDeploymentVersion",
  checkDeploymentVersionCompleted: "checkDeploymentVersionCompleted",
  checkDeploymentVersionMismatch: "checkDeploymentVersionMismatch",
  checkDeploymentVersionFailed: "checkDeploymentVersionFailed"
}

export const actionFactory = {
  onAppContextMounted: () => ({type: actionTypes.onAppContextMounted}),
  onLoadApiKey: () => ({type: actionTypes.onLoadApiKey}),
  onLoadApiKeyCompleted: payload => ({type: actionTypes.onLoadApiKeyCompleted, payload}),
  onLoadApiKeyFailed: payload => ({type: actionTypes.onLoadApiKeyFailed, payload}),
  onTokenUpdated: payload => ({type: actionTypes.onTokenUpdated, payload}),
  onTokenSubmitted: () => ({type: actionTypes.onTokenSubmitted}),
  onNoTokenFound: () => ({type: actionTypes.onNoTokenFound}),
  onTenantConfigUpdated: payload => ({type: actionTypes.onTenantConfigUpdated, payload}),
  exchangeToken: payload => ({type: actionTypes.exchangeToken, payload}),
  exchangeTokenCompleted: payload => ({type: actionTypes.exchangeTokenCompleted, payload}),
  exchangeTokenFailed: payload => ({type: actionTypes.exchangeTokenFailed, payload}),
  cameraMounted: () => ({type: actionTypes.cameraMounted}),
  cameraOpening: () => ({type: actionTypes.cameraOpening}),
  cameraOpened: () => ({type: actionTypes.cameraOpened}),
  cameraUnmounted: () => ({type: actionTypes.cameraUnmounted}),
  captureJourneyStarted: () => ({type: actionTypes.captureJourneyStarted}),
  faceCaptureInitStarted: () => ({type: actionTypes.faceCaptureInitStarted}),
  faceCaptureInitialized: () => ({type: actionTypes.faceCaptureInitialized}),
  faceCaptureCapturing: () => ({type: actionTypes.faceCaptureCapturing}),
  faceCaptureCaptured: () => ({type: actionTypes.faceCaptureCaptured}),
  blockedCameraDetected: payload => ({type: actionTypes.blockedCameraDetected, payload}),
  blockedCameraDetectedFailed: payload => ({type: actionTypes.blockedCameraDetectedFailed, payload}),
  blockedCameraDetectedCompleted: payload => ({type: actionTypes.blockedCameraDetectedCompleted, payload}),
  captureLiveFaceFailedToLoad: () => ({type: actionTypes.captureLiveFaceFailedToLoad}),
  logUserJourneyEvent: payload => ({type: actionTypes.logUserJourneyEvent, payload}),
  logUserJourneyEventCompleted: payload => ({type: actionTypes.logUserJourneyEventCompleted, payload}),
  logUserJourneyEventFailed: payload => ({type: actionTypes.logUserJourneyEventFailed, payload}),
  captureLiveFaceTimedOut: () => ({type: actionTypes.captureLiveFaceTimedOut}),
  onUnexpectedCaptureLiveFaceError: (payload) => ({type: actionTypes.onUnexpectedCaptureLiveFaceError, payload}),
  noConfigurationProfileFound: (payload) => ({type: actionTypes.noConfigurationProfileFound, payload}),
  cameraInitializationTimedOut: () => ({type: actionTypes.cameraInitializationTimedOut}),
  resetCameraInitialization: () => ({type: actionTypes.resetCameraInitialization}),
  cameraErrorReceived: payload => ({type: actionTypes.cameraErrorReceived, payload}),
  cameraFailureClassified: payload => ({type: actionTypes.cameraFailureClassified, payload}),
  watchCameraPermission: () => ({type: actionTypes.watchCameraPermission}),
  unwatchCameraPermission: () => ({type: actionTypes.unwatchCameraPermission}),
  cameraPermissionStateChanged: payload => ({type: actionTypes.cameraPermissionStateChanged, payload}),
  captureLocationRequested: () => ({type: actionTypes.captureLocationRequested}),
  captureLocationSuccess: payload => ({type: actionTypes.captureLocationSuccess, payload}),
  onLoadWebAuthnCapabilities: () => ({type: actionTypes.onLoadWebAuthnCapabilities}),
  onLoadWebAuthnCapabilitiesCompleted: payload => ({type: actionTypes.onLoadWebAuthnCapabilitiesCompleted, payload}),
  onLoadWebAuthnCapabilitiesFailed: payload => ({type: actionTypes.onLoadWebAuthnCapabilitiesFailed, payload}),
  sdkLoadStarted: () => ({type: actionTypes.sdkLoadStarted}),
  sdkLoadCompleted: () => ({type: actionTypes.sdkLoadCompleted}),
  sdkLoadFailed: payload => ({type: actionTypes.sdkLoadFailed, payload}),
  cdnLoadRetryAttempt: payload => ({type: actionTypes.cdnLoadRetryAttempt, payload}),
  cdnLoadSuccess: () => ({type: actionTypes.cdnLoadSuccess}),
  cdnLoadFailedAfterRetries: payload => ({type: actionTypes.cdnLoadFailedAfterRetries, payload}),
  // User Account Identifiers actions
  recordedIdentifierSelected: () => ({type: actionTypes.recordedIdentifierSelected}),
  manualIdentifierSelected: () => ({type: actionTypes.manualIdentifierSelected}),
  checkDeploymentVersion: () => ({type: actionTypes.checkDeploymentVersion}),
  checkDeploymentVersionCompleted: () => ({type: actionTypes.checkDeploymentVersionCompleted}),
  checkDeploymentVersionMismatch: payload => ({type: actionTypes.checkDeploymentVersionMismatch, payload}),
  checkDeploymentVersionFailed: payload => ({type: actionTypes.checkDeploymentVersionFailed, payload})
}

// Define pairs of events to measure time between them
export const timingPairs = [
  {
    startAction: actionTypes.faceCaptureInitStarted,
    endAction: actionTypes.faceCaptureInitialized,
    metricName: 'face_capture_init_duration_milliseconds'
  },
  {
    startAction: actionTypes.cameraOpening,
    endAction: actionTypes.cameraOpened,
    metricName: 'first_camera_open_duration_milliseconds'
  },
  {
    startAction: actionTypes.captureJourneyStarted,
    endAction: actionTypes.cameraOpened,
    metricName: 'time_to_first_camera_milliseconds'
  },
  {
    startAction: actionTypes.faceCaptureCapturing,
    endAction: actionTypes.faceCaptureCaptured,
    metricName: 'capture_processing_duration_milliseconds'
  },
  {
    startAction: identityActionTypes.uploadSecureCaptureSelfie,
    endAction: identityActionTypes.uploadSecureCaptureSelfieCompleted,
    metricName: 'selfie_submission_duration_milliseconds'
  },
  {
    startAction: actionTypes.captureJourneyStarted,
    endAction: identityActionTypes.uploadSecureCaptureSelfieCompleted,
    metricName: 'full_journey_duration_milliseconds'
  },
  {
    startAction: actionTypes.sdkLoadStarted,
    endAction: actionTypes.sdkLoadCompleted,
    metricName: 'cdn_sdk_load_duration_milliseconds'
  },
  {
    startAction: incomeActionTypes.uploadIncomeDocument,
    endAction: incomeActionTypes.uploadIncomeDocumentCompleted,
    metricName: 'upload_income_document_duration_milliseconds'
  }
]

const reducer = (state, action) => produce(state, draft => {
  const {type, payload} = action

  switch (type) {
    case actionTypes.onLoadApiKey:
      draft.loadApiKeyInProgress = true
      break

    case actionTypes.onLoadApiKeyCompleted:
      draft.loadApiKeyInProgress = false
      draft.apiKey = payload.apiKey
      draft.loadApiKeyFailed = undefined
      break

    case actionTypes.onLoadApiKeyFailed:
      draft.loadApiKeyInProgress = false
      draft.loadApiKeyFailed = payload
      break

    case actionTypes.onTokenUpdated:
      draft.token = payload.token
      if (payload.token !== undefined) {
        draft.noTokenFound = false
      }
      break

    case actionTypes.onTokenSubmitted:
      draft.exchangeTokenFailure = undefined
      break

    case actionTypes.onNoTokenFound:
      draft.noTokenFound = true
      break

    case actionTypes.onTenantConfigUpdated:
      draft.tenantConfig = payload.tenantConfig
      draft.theme = generateTheme(payload.tenantConfig.pallet)
      break

    case actionTypes.exchangeToken:
      draft.exchangeTokenLoading = true
      draft.tokenState = undefined
      draft.exchangeTokenFailure = undefined
      break

    case actionTypes.exchangeTokenCompleted:
      draft.exchangeTokenLoading = false
      draft.tokenState = payload
      draft.exchangeTokenFailure = undefined
      break

    case actionTypes.exchangeTokenFailed:
      draft.exchangeTokenLoading = false
      draft.exchangeTokenFailure = classifyExchangeTokenFailure(payload.error)
      break

    case actionTypes.recordedIdentifierSelected:
    case actionTypes.manualIdentifierSelected:
      break

    case actionTypes.cameraInitializationTimedOut:
      draft.cameraInitializationTimedOut = true
      break

    case actionTypes.resetCameraInitialization:
      draft.cameraInitializationTimedOut = false
      break

    // These retries navigate straight to the capture route without passing the landing
    // page, so a stale classification must not bounce them back to the failure page
    case identityActionTypes.onTrySecureCaptureSelfieAgainClicked:
    case identityActionTypes.unexpectedErrorTryAgainClicked:
    case identityActionTypes.manualCaptureSelected:
    case identityActionTypes.autoCaptureSelected:
      draft.cameraFailure = undefined
      break

    case actionTypes.cameraFailureClassified:
      draft.cameraFailure = payload
      // finaliseAttempt no-ops before the camera opened, so a prompt-stage denial
      // does not count as an attempt
      if (ENVIRONMENT_FAILURES.includes(payload.failureType)) {
        finaliseAttempt(draft, payload.failureType)
      }
      break

    case actionTypes.watchCameraPermission:
    case actionTypes.unwatchCameraPermission:
      draft.cameraPermissionState = undefined
      break

    case actionTypes.cameraPermissionStateChanged:
      draft.cameraPermissionState = payload.state
      break

    case actionTypes.cameraMounted:
      draft.cameraMounted = true
      draft.identityState.currentAttempt = {
        attemptNumber: (draft.identityState.attempts?.length || 0) + 1,
        faceHintCount: 0,
        perfectHintCount: 0,
        cameraOpenedAt: undefined
      }
      break

    case actionTypes.cameraUnmounted:
      draft.cameraMounted = false
      break

    case actionTypes.cameraOpening:
      if (!draft.timingMap[actionTypes.cameraOpening]) {
        draft.timingMap[actionTypes.cameraOpening] = new Date().getTime()
      }
      break

    case actionTypes.cameraOpened: {
      const now = new Date().getTime()
      if (!draft.timingMap[actionTypes.cameraOpened]) {
        draft.timingMap[actionTypes.cameraOpened] = now
      }
      if (draft.identityState.currentAttempt && !draft.identityState.currentAttempt.cameraOpenedAt) {
        draft.identityState.currentAttempt.cameraOpenedAt = now
      }
      break
    }

    case actionTypes.captureJourneyStarted:
      if (!draft.timingMap[actionTypes.captureJourneyStarted]) {
        draft.timingMap[actionTypes.captureJourneyStarted] = new Date().getTime()
      }
      draft.cameraFailure = undefined
      break

    case actionTypes.captureLiveFaceTimedOut:
      draft.identityState.timeoutPageCount = (draft.identityState.timeoutPageCount || 0) + 1
      finaliseAttempt(draft, "timeout")
      break

    case actionTypes.onUnexpectedCaptureLiveFaceError:
      finaliseAttempt(draft, "error")
      break

    case actionTypes.faceCaptureInitStarted:
      draft.timingMap[actionTypes.faceCaptureInitStarted] = new Date().getTime()
      break

    case actionTypes.faceCaptureInitialized:
      draft.timingMap[actionTypes.faceCaptureInitialized] = new Date().getTime()
      break

    case actionTypes.faceCaptureCapturing:
      draft.timingMap[actionTypes.faceCaptureCapturing] = new Date().getTime()
      break

    case actionTypes.faceCaptureCaptured:
      draft.timingMap[actionTypes.faceCaptureCaptured] = new Date().getTime()
      break

    case actionTypes.sdkLoadStarted:
      draft.sdkLoading = true
      draft.sdkLoadFailed = false
      draft.timingMap[actionTypes.sdkLoadStarted] = new Date().getTime()
      break

    case actionTypes.sdkLoadCompleted:
      draft.sdkLoading = false
      draft.sdkLoaded = true
      draft.timingMap[actionTypes.sdkLoadCompleted] = new Date().getTime()
      break

    case actionTypes.sdkLoadFailed:
      draft.sdkLoading = false
      draft.sdkLoaded = false
      draft.sdkLoadFailed = true
      draft.sdkError = payload
      break

    case actionTypes.cdnLoadRetryAttempt:
      draft.cdnRetryAttempts = (draft.cdnRetryAttempts || 0) + 1
      draft.cdnRetryDetails = payload
      break

    case actionTypes.cdnLoadSuccess:
      draft.cdnLoadSuccess = true
      draft.cdnLoadFailedAfterRetries = false
      break

    case actionTypes.cdnLoadFailedAfterRetries:
      draft.cdnLoadFailedAfterRetries = true
      draft.cdnLoadSuccess = false
      draft.cdnLoadError = payload
      break

    case actionTypes.onLoadWebAuthnCapabilitiesCompleted:
      draft.webAuthnCapabilities = payload
      break

    case actionTypes.onLoadWebAuthnCapabilitiesFailed:
      draft.webAuthnCapabilities = {error: payload.error}
      break

    case actionTypes.checkDeploymentVersion:
      draft.versionCheckInProgress = true
      draft.versionMismatchDetected = false
      break

    case actionTypes.checkDeploymentVersionCompleted:
      draft.versionCheckInProgress = false
      draft.versionMismatchDetected = false
      break

    case actionTypes.checkDeploymentVersionMismatch:
      draft.versionCheckInProgress = false
      draft.versionMismatchDetected = true
      draft.serverVersion = payload.serverVersion
      draft.clientVersion = payload.clientVersion
      break

    case actionTypes.checkDeploymentVersionFailed:
      draft.versionCheckInProgress = false
      draft.versionCheckFailed = payload
      break

    default:
    // do nothing
  }
})

export const appReducer = (state, action) => reducer(identityReducer(incomeReducer(state, action), action), action)